import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IntlProvider } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import { appLocales, DEFAULT_LOCALE, translationMessages } from '../messages';

const RTL_LOCALES = ['ar', 'he-IL'];

// Language code mappings - maps backend codes (.locales.json) to frontend codes (appLocales)
const LANGUAGE_CODE_MAPPING: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  bg: 'bg-BG',
  cs: 'cs-CZ',
  de: 'de-DE',
  el: 'el-GR',
  fi: 'fi-FI',
  fr: 'fr-FR',
  he: 'he-IL',
  'hi-in': 'hi-IN',
  id: 'id-ID',
  it: 'it-IT',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ms: 'ms-MY',
  nl: 'nl-NL',
  'nn-NO': 'no-NO',
  pl: 'pl-PL',
  pt: 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-RU',
  sv: 'sv-SE',
  th: 'th-TH',
  tr: 'tr-TR',
  vi: 'vi-VN',
};

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string) => void;
  setDefaultLocale: (locale: string | null) => void;
  setLanguageConfig: (config: {
    configuredLanguages: string[];
    isLanguageSelectorEnabled: boolean;
  }) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

interface LanguageProviderProps {
  children: React.ReactNode;
}

// Function to check if a locale is available in our translations
const isLocaleAvailable = (locale: string): boolean => {
  return appLocales.includes(locale);
};

// Function to check if a locale is configured by admin
const isLocaleConfigured = (
  locale: string,
  configuredLanguages: string[],
): boolean => {
  if (!configuredLanguages || configuredLanguages.length === 0) {
    return false;
  }
  return configuredLanguages.some(configuredLang => {
    const mappedCode = LANGUAGE_CODE_MAPPING[configuredLang] ?? configuredLang;
    return mappedCode === locale || configuredLang === locale;
  });
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const history = useHistory();
  const { pathname, search } = location;

  const [defaultLocale, setDefaultLocale] = useState<string | null>(null);
  const [configuredLanguages, setConfiguredLanguages] = useState<string[]>([]);
  const [isLanguageSelectorEnabled, setIsLanguageSelectorEnabled] =
    useState<boolean>(false);
  const [hasConfig, setHasConfig] = useState(false);
  const hasProcessedLocaleRef = useRef(false);

  // Initialize with URL locale if valid, otherwise DEFAULT_LOCALE
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    const params = new URLSearchParams(search);
    const localeParam = params.get('locale');
    if (localeParam && isLocaleAvailable(localeParam)) {
      return localeParam;
    }
    return DEFAULT_LOCALE;
  });

  // Function to set language configuration from brandConfig
  const setLanguageConfig = useCallback(
    (config: {
      configuredLanguages: string[];
      isLanguageSelectorEnabled: boolean;
    }) => {
      setConfiguredLanguages(config.configuredLanguages);
      setIsLanguageSelectorEnabled(config.isLanguageSelectorEnabled);
      setHasConfig(true);
    },
    [],
  );

  // Helper to get the admin's fallback locale
  const getAdminFallbackLocale = useCallback(() => {
    // Use admin's default_locale if available, otherwise en-US
    if (defaultLocale && isLocaleAvailable(defaultLocale)) {
      return defaultLocale;
    }
    return DEFAULT_LOCALE;
  }, [defaultLocale]);

  // Update the URL with the selected language as a query param
  const setLanguage = useCallback(
    (language: string, updateUrl = true) => {
      const localeToUse = isLocaleAvailable(language)
        ? language
        : DEFAULT_LOCALE;

      setCurrentLanguage(localeToUse);

      if (updateUrl) {
        const params = new URLSearchParams(search);
        params.set('locale', localeToUse);
        history.replace({
          pathname,
          search: params.toString(),
        });
      }
    },
    [history, pathname, search],
  );

  // Main locale determination logic - runs AFTER we have config
  useEffect(() => {
    // Wait until we have the configuration from brandConfig
    if (!hasConfig) {
      return;
    }

    // Only process once
    if (hasProcessedLocaleRef.current) {
      return;
    }
    hasProcessedLocaleRef.current = true;

    const params = new URLSearchParams(search);
    const urlLocale = params.get('locale');

    if (!isLanguageSelectorEnabled) {
      // Always use en-US, don't put locale in URL
      setCurrentLanguage(DEFAULT_LOCALE);
      // Remove locale from URL since it can't be changed
      if (urlLocale) {
        params.delete('locale');
        history.replace({
          pathname,
          search: params.toString(),
        });
      }
      return;
    }

    const adminFallback = getAdminFallbackLocale();

    // 2a. URL has a locale parameter
    if (urlLocale && isLocaleAvailable(urlLocale)) {
      // Check if it's in configured languages
      if (isLocaleConfigured(urlLocale, configuredLanguages)) {
        // Valid and configured - use it
        setLanguage(urlLocale, false);
        return;
      }
      setLanguage(adminFallback);
      return;
    }

    setLanguage(adminFallback);
  }, [
    hasConfig,
    configuredLanguages,
    isLanguageSelectorEnabled,
    search,
    pathname,
    history,
    setLanguage,
    getAdminFallbackLocale,
  ]);

  const mappedLocale = currentLanguage || DEFAULT_LOCALE;
  const messages =
    translationMessages[mappedLocale] || translationMessages[DEFAULT_LOCALE];

  useEffect(() => {
    if (RTL_LOCALES.includes(mappedLocale)) {
      document.body.setAttribute('dir', 'rtl');
    } else {
      document.body.setAttribute('dir', 'ltr');
    }
  }, [mappedLocale]);

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setLanguage,
        setDefaultLocale,
        setLanguageConfig,
      }}
    >
      <IntlProvider
        locale={mappedLocale}
        key={mappedLocale}
        messages={messages}
        textComponent={React.Fragment}
        defaultRichTextElements={{
          b: chunks => <b>{chunks}</b>,
        }}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
